<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useId, watch } from 'vue';
import {
  DEFAULT_IMAGE_TYPES,
  addImages,
  canPreview,
  carriesFiles,
  filesOf,
  formatBytes,
  pastedFileName,
  remainingLabel,
} from '../imageFiles';

/**
 * Lot AQ : zone d'images réutilisable (maquette retours-1410, onglet A, repères 4-6). On ajoute des images en les
 * glissant (sur la zone ou n'importe où dans le formulaire qui la contient), en les collant (Cmd/Ctrl+V quand le
 * formulaire a le focus : une capture d'écran du presse-papiers suffit) ou en les choisissant. Miniatures avec ×,
 * compteur, messages clairs. `v-model` : la liste des fichiers retenus. Rien de propre aux retours (lot AS).
 */
const props = withDefaults(
  defineProps<{
    modelValue: File[];
    maxCount?: number;
    maxBytes?: number;
    types?: readonly string[];
    disabled?: boolean;
    /**
     * Lot AS : images déjà enregistrées ailleurs (carte « Tâche » dépliée) : elles comptent dans `maxCount` et
     * dans le compteur ; leurs miniatures sont affichées par le parent.
     */
    existing?: number;
  }>(),
  {
    maxCount: 5,
    maxBytes: 5 * 1024 * 1024,
    types: () => DEFAULT_IMAGE_TYPES,
    disabled: false,
    existing: 0,
  },
);
const emit = defineEmits<{ 'update:modelValue': [files: File[]] }>();

const inputId = useId();
const root = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const errors = ref<string[]>([]);
const over = ref(false);
let dragDepth = 0;
let scope: HTMLElement | null = null;

const full = computed(() => props.modelValue.length + props.existing >= props.maxCount);
const accept = computed(() => [...props.types, '.heic', '.heif'].join(','));
const pasteKey =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent)
    ? 'Cmd+V'
    : 'Ctrl+V';

/** Aperçus : une adresse locale par image affichable, libérée quand l'image quitte la liste. */
const previews = shallowRef(new Map<File, string>());
const canMakeUrls = typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
function syncPreviews(files: readonly File[]): void {
  const next = new Map<File, string>();
  for (const file of files) {
    const existing = previews.value.get(file);
    if (existing) next.set(file, existing);
    else if (canMakeUrls && canPreview(file)) next.set(file, URL.createObjectURL(file));
  }
  for (const [file, url] of previews.value) if (!next.has(file)) URL.revokeObjectURL(url);
  previews.value = next;
}
watch(() => props.modelValue, syncPreviews, { immediate: true });

function add(incoming: File[]): void {
  if (props.disabled || incoming.length === 0) return;
  const result = addImages(props.modelValue, incoming, {
    maxCount: Math.max(0, props.maxCount - props.existing),
    maxBytes: props.maxBytes,
    types: props.types,
  });
  const room = Math.max(0, props.maxCount - props.existing);
  // Avec des images déjà enregistrées, le message dit la vraie limite (« 5 images au plus »).
  errors.value =
    props.existing > 0
      ? result.errors.map((m) => m.replace(`${room} images au plus`, `${props.maxCount} images au plus`))
      : result.errors;
  if (result.files.length !== props.modelValue.length) emit('update:modelValue', result.files);
}

function remove(file: File): void {
  errors.value = [];
  emit(
    'update:modelValue',
    props.modelValue.filter((f) => f !== file),
  );
}

function onPick(event: Event): void {
  const target = event.target as HTMLInputElement;
  add(Array.from(target.files ?? []));
  // Pour pouvoir rechoisir le même fichier après l'avoir retiré.
  target.value = '';
}

function onPaste(event: ClipboardEvent): void {
  const files = filesOf(event.clipboardData);
  if (files.length === 0) return; // un texte collé suit son chemin habituel
  event.preventDefault();
  const start = props.modelValue.length;
  add(
    files.map((file, k) => {
      const name = pastedFileName(file.name, file.type, start + k + 1);
      return name === file.name
        ? file
        : new File([file], name, { type: file.type, lastModified: Date.now() });
    }),
  );
}

function onDragEnter(event: DragEvent): void {
  if (!carriesFiles(event.dataTransfer) || props.disabled) return;
  event.preventDefault();
  dragDepth += 1;
  over.value = true;
}
function onDragOver(event: DragEvent): void {
  if (!carriesFiles(event.dataTransfer) || props.disabled) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
}
function onDragLeave(event: DragEvent): void {
  if (!carriesFiles(event.dataTransfer)) return;
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) over.value = false;
}
function onDrop(event: DragEvent): void {
  if (!carriesFiles(event.dataTransfer)) return;
  event.preventDefault();
  dragDepth = 0;
  over.value = false;
  add(filesOf(event.dataTransfer));
}
/** Une image lâchée à côté de la zone ne doit pas remplacer la page (et le brouillon) par l'image. */
function onWindowDrop(event: DragEvent): void {
  if (carriesFiles(event.dataTransfer)) event.preventDefault();
}

onMounted(() => {
  scope = root.value?.closest('form') ?? root.value;
  if (!scope) return;
  scope.addEventListener('paste', onPaste);
  scope.addEventListener('dragenter', onDragEnter);
  scope.addEventListener('dragover', onDragOver);
  scope.addEventListener('dragleave', onDragLeave);
  scope.addEventListener('drop', onDrop);
  window.addEventListener('dragover', onWindowDrop);
  window.addEventListener('drop', onWindowDrop);
});
onBeforeUnmount(() => {
  if (scope) {
    scope.removeEventListener('paste', onPaste);
    scope.removeEventListener('dragenter', onDragEnter);
    scope.removeEventListener('dragover', onDragOver);
    scope.removeEventListener('dragleave', onDragLeave);
    scope.removeEventListener('drop', onDrop);
  }
  window.removeEventListener('dragover', onWindowDrop);
  window.removeEventListener('drop', onWindowDrop);
  for (const url of previews.value.values()) URL.revokeObjectURL(url);
  previews.value = new Map();
});

function typeLabel(file: File): string {
  return /\.heif$/i.test(file.name) || file.type === 'image/heif' ? 'HEIF' : 'HEIC';
}
</script>

<template>
  <div ref="root" class="idz" data-testid="image-drop-zone">
    <div class="idz-drop" :class="{ over, full: full && !over }" data-testid="image-drop-target">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="4" width="18" height="15" rx="2" />
        <circle cx="9" cy="9.5" r="1.6" />
        <path d="M21 15.5l-5-5L6 19" />
      </svg>
      <span v-if="over" class="idz-text">
        <b>Lâche pour ajouter</b>
        <span>{{ remainingLabel(modelValue.length + existing, maxCount) }}</span>
      </span>
      <span v-else class="idz-text idz-wide">
        <b>Glisse tes captures ici ou colle-les ({{ pasteKey }})</b>
        <span>ou <label :for="inputId" class="idz-pick">choisir des fichiers</label></span>
      </span>
      <label v-if="!over" :for="inputId" class="idz-text idz-narrow"><b>Ajouter des captures</b></label>
      <input
        :id="inputId"
        ref="input"
        class="sr-only"
        type="file"
        multiple
        :accept="accept"
        :disabled="disabled"
        data-testid="image-drop-input"
        @change="onPick"
      />
    </div>
    <ul v-if="modelValue.length" class="idz-thumbs" aria-label="Images ajoutées">
      <li
        v-for="file in modelValue"
        :key="`${file.name}:${file.size}:${file.lastModified}`"
        class="idz-thumb"
      >
        <img v-if="previews.get(file)" :src="previews.get(file)" :alt="file.name" />
        <span v-else class="idz-fallback" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linejoin="round"
          >
            <rect x="3" y="4" width="18" height="15" rx="2" />
            <circle cx="9" cy="9.5" r="1.6" />
            <path d="M21 15.5l-5-5L6 19" />
          </svg>
          {{ typeLabel(file) }}
        </span>
        <span class="idz-name">{{ file.name }} · {{ formatBytes(file.size) }}</span>
        <button
          type="button"
          class="idz-x"
          :aria-label="`Retirer ${file.name}`"
          :disabled="disabled"
          @click="remove(file)"
        >
          ×
        </button>
      </li>
    </ul>
    <div class="idz-count">
      <span data-testid="image-drop-count"
        ><b>{{ modelValue.length + existing }} / {{ maxCount }}</b> images</span
      >
      <span>{{ formatBytes(maxBytes) }} au plus chacune</span>
    </div>
    <p v-if="errors.length" class="idz-err" role="alert" data-testid="image-drop-errors">
      <template v-for="(message, k) in errors" :key="k"
        >{{ message }}<br v-if="k < errors.length - 1"
      /></template>
    </p>
  </div>
</template>

<style scoped>
.idz {
  display: grid;
  gap: 8px;
}
.idz-drop {
  position: relative;
  border: 1.5px dashed var(--line);
  border-radius: var(--radius);
  padding: 14px 12px;
  display: grid;
  justify-items: center;
  gap: 4px;
  text-align: center;
  color: var(--muted);
  font-size: 12.5px;
  background: var(--bg);
  transition:
    background 0.15s,
    border-color 0.15s;
}
.idz-drop:focus-within {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.idz-drop svg {
  width: 22px;
  height: 22px;
  color: var(--ghost);
}
.idz-drop b {
  color: var(--ink);
  font-weight: 600;
  font-size: 13px;
}
.idz-text {
  display: grid;
  gap: 2px;
}
.idz-narrow {
  display: none;
  cursor: pointer;
}
.idz-pick {
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
}
.idz-drop.over {
  border-color: var(--accent);
  border-style: solid;
  background: var(--accent-soft);
  color: var(--accent);
}
.idz-drop.over svg,
.idz-drop.over b {
  color: var(--accent);
}
.idz-drop.full {
  opacity: 0.55;
}
.idz-thumbs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
}
.idz-thumb {
  position: relative;
  border: 1px solid var(--line);
  border-radius: 8px;
  overflow: hidden;
  background: var(--panel);
  min-width: 0;
}
.idz-thumb img,
.idz-fallback {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
}
.idz-fallback {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 2px;
  background: var(--bg);
  color: var(--muted);
  font: 600 11px var(--f-mono);
}
.idz-fallback svg {
  width: 22px;
  height: 22px;
  color: var(--ghost);
}
.idz-name {
  display: block;
  font: 10.5px var(--f-mono);
  color: var(--muted);
  padding: 3px 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.idz-x {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 0;
  background: rgb(19 32 45 / 0.78);
  color: #fff;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
}
.idz-x:hover,
.idz-x:focus-visible {
  background: var(--bad);
}
.idz-count {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--muted);
}
.idz-count b {
  font-family: var(--f-mono);
  color: var(--ink);
  font-weight: 500;
}
.idz-err {
  margin: 0;
  font-size: 12px;
  color: var(--bad);
}
/* Sur téléphone : la zone devient « Ajouter des captures » (photos ou fichiers du téléphone). */
@media (max-width: 760px) {
  .idz-drop {
    padding: 9px 10px;
    grid-auto-flow: column;
    justify-content: start;
    text-align: left;
    gap: 8px;
  }
  .idz-wide {
    display: none;
  }
  .idz-narrow {
    display: block;
  }
  .idz-thumbs {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
